/**
 * `GET /v1/recalls/{id}/evidence` — the recall's evidence record, plan 3R U51.
 *
 * REST, because tRPC carries no file. A plain link cannot send the org header that REST
 * requires (`orgHeaders()`), so this fetches and hands back a blob, as `artwork.ts` does.
 */
import type { ProblemBody } from "@packagepage/shared";

import { orgHeaders } from "./client.js";

export type EvidenceFormat = "pdf" | "csv";

export type EvidenceOutcome =
  | { readonly ok: true; readonly blob: Blob; readonly filename: string }
  | { readonly ok: false; readonly problem: ProblemBody | null };

export const evidencePath = (recallId: string, format: EvidenceFormat): string =>
  `/v1/recalls/${encodeURIComponent(recallId)}/evidence?format=${format}`;

export function evidenceFilename(
  disposition: string | null,
  recallId: string,
  format: EvidenceFormat,
): string {
  const match = disposition === null ? null : /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? `recall-evidence-${recallId}.${format}`;
}

export async function requestEvidence(
  recallId: string,
  format: EvidenceFormat,
): Promise<EvidenceOutcome> {
  const response = await fetch(evidencePath(recallId, format), { headers: orgHeaders() });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const problem =
      typeof body === "object" && body !== null && "code" in body
        ? (body as ProblemBody)
        : null;
    return { ok: false, problem };
  }
  return {
    ok: true,
    blob: await response.blob(),
    filename: evidenceFilename(
      response.headers.get("content-disposition"),
      recallId,
      format,
    ),
  };
}
